import { shallowEqual } from 'react-redux';

import { Flex, Grid, Spacer } from '@zillow/constellation';
import { Box } from '@zillow/constellation-10';
import { LoadingParagraph, LoadingText, shimmerStyles } from '@zg-rentals/loading-components';

import { useSelector } from '../../../selectors/useSelector';

import { screenSize as SCREEN_SIZE } from '../../../utils/browserUtils';
import { getIconByName } from '../../../utils/iconUtils';

import SimpleCard from '../../SimpleCard';

import NextStepsRecommendationsCSEvents from '../../../model/ClickstreamEvent/NextStepsRecommendationsCSEvents';

import { StyledDisplayContainer } from '../../StyledComponents/StyledLayout';
import { StyledImage } from '../../../StyledComponents/StyledComponents';
import { StyledContentContainer, StyledFlex } from './StyledComponents';
import { NoUnderlineAnchor, singleTextLineStyles } from '../../../styles';

import type { StrictInlineMediaQueryProp } from '@zillow/constellation';
import type { ParagraphPropsInterface, TextPropsInterface } from '@zillow/constellation-10';
import type { TileType } from '../../../model/NextSteps';
import type { CssProperties } from '@/styled-system/types';

const getIcon = (icon: string) => {
  const Icon = getIconByName({ name: icon });

  if (icon === 'DetailedIconHome') {
    return <StyledImage className="DetailedIconHome" src={Icon} alt="home icon" size={{ default: 'md', lg: 'lg' }} />;
  }

  return Icon && <Icon size={{ default: 'sm', lg: 'md' }} />;
};

type Props = {
  title?: string;
  titleFontType?: ParagraphPropsInterface['textStyle'];
  titleFontColor?: CssProperties['color'];
  titleMarginBottom?: '400' | '200';
  tileFontType?: TextPropsInterface['textStyle'];
  tileSubtitleFontType?: ParagraphPropsInterface['textStyle'];
  marginBottom?: typeof StrictInlineMediaQueryProp;
  tileList?: Array<TileType>;
  onTileClick?: ({ sectionTitle, id, url }: { sectionTitle: string; id: string; url: string }) => void;
  isLoading?: boolean;
};

const Section = ({
  tileList = [],
  title = 'Section',
  titleFontColor = 'text.neutral',
  titleFontType = 'body',
  titleMarginBottom = '200',
  tileFontType = 'body',
  tileSubtitleFontType = 'body-xs',
  marginBottom = 'xs',
  onTileClick = undefined,
  isLoading = false,
}: Props) => {
  const { constellationScreenSize } = useSelector((state) => {
    return {
      constellationScreenSize: state.app.constellationScreenSize,
    };
  }, shallowEqual);

  const tileListLength = tileList?.length || 0;
  const gridRepeat = tileListLength < 2 ? 1 : 2;
  const withChevron = constellationScreenSize === SCREEN_SIZE.XL || constellationScreenSize === SCREEN_SIZE.XXL;

  const handleClick = ({ id, url, sectionTitle }: { id: string; url: string; sectionTitle: string }) => {
    if (onTileClick) {
      onTileClick({ sectionTitle, id, url });
    }
  };

  const renderCard = (item: TileType, sectionTitle: string) => {
    const { id, title, subtitle, icon, url } = item;
    return (
      <SimpleCard
        cursor="pointer"
        withChevron={withChevron}
        onClick={() => {
          handleClick({ id, url, sectionTitle });
        }}
        isLoading={isLoading}
        data-eventid={NextStepsRecommendationsCSEvents.ClickNextStepsRecommendations.data_event_id}
      >
        <StyledFlex display="flex" flexDirection="row" alignItems="center">
          <Flex flexShrink={0}>
            <StyledDisplayContainer display={{ default: 'none', md: 'inline-flex' }}>
              <Spacer marginRight={{ default: 'xs', lg: 'md' }}>
                {isLoading ? (
                  <Box css={{ width: 'icon.lg', height: 'icon.lg', ...shimmerStyles }} data-loading={true} />
                ) : (
                  getIcon(icon)
                )}
              </Spacer>
            </StyledDisplayContainer>
          </Flex>
          <StyledContentContainer>
            <LoadingText
              textStyle={tileFontType}
              title={title}
              isLoading={isLoading}
              loadingWidth="60%"
              css={{ lg: { fontSize: 'body-sm', lineHeight: 'body-sm' }, ...singleTextLineStyles }}
            >
              {title}
            </LoadingText>
            {subtitle && (
              <LoadingParagraph
                textStyle={tileSubtitleFontType}
                title={subtitle}
                isLoading={isLoading}
                loadingWidth="60%"
                css={{ lg: { fontSize: 'body-sm', lineHeight: 'body-sm' }, ...singleTextLineStyles }}
              >
                {subtitle}
              </LoadingParagraph>
            )}
          </StyledContentContainer>
        </StyledFlex>
      </SimpleCard>
    );
  };

  const renderTile = ({ item, index }: { item: TileType; index: number }) => {
    const { url } = item;
    return (
      <Grid key={index} data-testid="TileItem">
        {url ? <NoUnderlineAnchor href={url}>{renderCard(item, title)}</NoUnderlineAnchor> : renderCard(item, title)}
      </Grid>
    );
  };

  return (
    <Spacer as="div" data-testid="Section" marginBottom={marginBottom}>
      <LoadingParagraph
        textStyle={titleFontType}
        loadingWidth="50%"
        isLoading={isLoading && !title}
        css={{ color: titleFontColor, marginBottom: titleMarginBottom }}
      >
        {title}
      </LoadingParagraph>

      <Grid
        data-testid="TileListContainer"
        display="grid"
        gridTemplateColumns={{
          default: `repeat(${gridRepeat}, minmax(0,1fr))`,
          lg_gte: `repeat(${gridRepeat}, minmax(0,1fr))`,
          xl_gte: `repeat(1, minmax(0,1fr))`,
        }}
        gap={{ default: 'xs', lg: 'sm' }}
      >
        {tileList &&
          tileList.map((item, index) => {
            return renderTile({ item, index });
          })}
      </Grid>
    </Spacer>
  );
};

export default Section;
